import { newFrameElement } from "@excalidraw/excalidraw/element/newElement";
import { newElementWith } from "@excalidraw/excalidraw/element/mutateElement";
import { syncInvalidIndices } from "@excalidraw/excalidraw/fractionalIndex";
import {
  MAX_PRESENTATION_FRAMES,
  PRESENTATION_FRAME_HEIGHT,
  PRESENTATION_FRAME_WIDTH,
  frameByPosition,
  isPresentationFirstSlide,
  presentationFirstSlideId,
  presentationSlides,
  presentationSlotAt,
} from "@canvaschool/contracts";

import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
} from "@excalidraw/excalidraw/element/types";
import type { SerializedExcalidrawElement } from "@canvaschool/contracts";

/**
 * Slide arithmetic for a presentation canvas (발표 캔버스, plan 2026-10-28).
 * Position is the order: the frames sit on slots 0..n-1 of one row and each
 * is named for its slot, "1".."n". Every operation below returns the whole
 * scene (deleted elements included) with that layout restored, so a swap, a
 * delete or an add also repairs a row a stale copy had left crooked. The
 * caller applies the result in one `updateScene`, outside the undo history.
 */

const serializedOf = (element: ExcalidrawElement) =>
  element as unknown as SerializedExcalidrawElement;

/** The live slides in slide order. */
export const slideFrames = (
  elements: readonly ExcalidrawElement[],
): ExcalidrawFrameLikeElement[] =>
  presentationSlides(
    elements.filter((element) => element.type === "frame").map(serializedOf),
  ) as unknown as ExcalidrawFrameLikeElement[];

/** Whether another slide still fits. */
export const canAddSlide = (elements: readonly ExcalidrawElement[]): boolean =>
  slideFrames(elements).length < MAX_PRESENTATION_FRAMES;

/** What belongs to each slide: the elements whose center lies inside it, and
 * a label with its container. Elements in no slide stay where they are. */
const membersByFrame = (
  elements: readonly ExcalidrawElement[],
  frames: readonly ExcalidrawFrameLikeElement[],
): Map<string, ExcalidrawElement[]> => {
  const serializedFrames = frames.map(serializedOf);
  const frameOf = new Map<string, string>();
  for (const element of elements) {
    if (
      element.isDeleted ||
      element.type === "frame" ||
      element.type === "magicframe" ||
      ("containerId" in element && element.containerId)
    ) {
      continue;
    }
    const frame = frameByPosition(serializedOf(element), serializedFrames);
    if (frame) {
      frameOf.set(element.id, frame.id);
    }
  }
  const members = new Map<string, ExcalidrawElement[]>(
    frames.map((frame) => [frame.id, []]),
  );
  for (const element of elements) {
    if (
      element.isDeleted ||
      element.type === "frame" ||
      element.type === "magicframe"
    ) {
      continue;
    }
    const ownerId =
      "containerId" in element && element.containerId
        ? frameOf.get(element.containerId)
        : frameOf.get(element.id);
    if (ownerId) {
      members.get(ownerId)?.push(element);
    }
  }
  return members;
};

/** Puts `order` on slots 0..n-1 (each slide's contents moving along), names
 * them, and deletes `removed` slides with their contents. */
const layOut = (
  elements: readonly ExcalidrawElement[],
  order: readonly ExcalidrawFrameLikeElement[],
  members: Map<string, ExcalidrawElement[]>,
  removed: readonly ExcalidrawFrameLikeElement[] = [],
): ExcalidrawElement[] => {
  const patches = new Map<string, Record<string, unknown>>();
  order.forEach((frame, index) => {
    const slot = presentationSlotAt(index);
    patches.set(frame.id, { x: slot.x, y: slot.y, name: String(index + 1) });
    for (const member of members.get(frame.id) ?? []) {
      patches.set(member.id, {
        x: member.x + (slot.x - frame.x),
        y: member.y + (slot.y - frame.y),
        frameId: frame.id,
      });
    }
  });
  for (const frame of removed) {
    patches.set(frame.id, { isDeleted: true });
    for (const member of members.get(frame.id) ?? []) {
      patches.set(member.id, { isDeleted: true });
    }
  }
  return elements.map((element) => {
    const patch = patches.get(element.id);
    return patch ? newElementWith(element, patch as never) : element;
  });
};

/** Slide-sized frame at slot `index`. */
const newSlide = (index: number, id?: string): ExcalidrawElement => {
  const slot = presentationSlotAt(index);
  const frame = newFrameElement({
    name: String(index + 1),
    x: slot.x,
    y: slot.y,
    width: PRESENTATION_FRAME_WIDTH,
    height: PRESENTATION_FRAME_HEIGHT,
  });
  return id === undefined ? frame : { ...frame, id };
};

export type SlideEdit = {
  elements: ExcalidrawElement[];
  /** The slide the edit is about, as it stands in `elements`. */
  frame: ExcalidrawElement;
};

/** Appends a slide at the next slot. Null at the limit. */
export const addSlide = (
  elements: readonly ExcalidrawElement[],
  id?: string,
): SlideEdit | null => {
  const slides = slideFrames(elements);
  if (slides.length >= MAX_PRESENTATION_FRAMES) {
    return null;
  }
  const members = membersByFrame(elements, slides);
  const created = newSlide(slides.length, id);
  const laidOut = layOut(
    syncInvalidIndices([...elements, created]),
    slides,
    members,
  );
  return {
    elements: laidOut,
    frame: laidOut.find((element) => element.id === created.id) ?? created,
  };
};

/** The first slide's id, the same in every editor of one canvas: two tabs
 * laying it at once write one element, which the merge settles, rather than
 * two frames stacked on slot 0. */
export const firstSlideId = presentationFirstSlideId;

/** Whether `frame` is a first slide, which is never deleted. */
export const isFirstSlide = (frame: ExcalidrawElement): boolean =>
  isPresentationFirstSlide(serializedOf(frame));

/** A canvas with no slide gets one. Null when it already has one. The first
 * slide cannot be deleted, so no slide at all is a canvas an older build let
 * lose them (10-30: two students left without a slide for the rest of the
 * class); a deleted first slide is not revived but followed by the next
 * fixed id, still the same in every editor of the canvas. */
export const ensureFirstSlide = (
  elements: readonly ExcalidrawElement[],
  canvasId: string,
): SlideEdit | null => {
  if (slideFrames(elements).length > 0) {
    return null;
  }
  const taken = new Set(elements.map((element) => element.id));
  let id = firstSlideId(canvasId);
  for (let next = 2; taken.has(id); next += 1) {
    id = `${firstSlideId(canvasId)}-${next}`;
  }
  return addSlide(elements, id);
};

/** Swaps a slide with its neighbour (`-1` left, `1` right), contents and
 * all. Null at either end, or for a frame that is not a slide. */
export const swapSlide = (
  elements: readonly ExcalidrawElement[],
  frameId: string,
  direction: -1 | 1,
): SlideEdit | null => {
  const slides = slideFrames(elements);
  const from = slides.findIndex((frame) => frame.id === frameId);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= slides.length) {
    return null;
  }
  const members = membersByFrame(elements, slides);
  const order = [...slides];
  [order[from], order[to]] = [order[to], order[from]];
  const laidOut = layOut(elements, order, members);
  return {
    elements: laidOut,
    frame: laidOut.find((element) => element.id === frameId)!,
  };
};

/** Deletes a slide with what is drawn in it and pulls the later ones left.
 * Null for the only slide, the first slide, or a frame that is not a slide. */
export const deleteSlide = (
  elements: readonly ExcalidrawElement[],
  frameId: string,
): ExcalidrawElement[] | null => {
  const slides = slideFrames(elements);
  const target = slides.find((frame) => frame.id === frameId);
  if (!target || slides.length < 2 || isFirstSlide(target)) {
    return null;
  }
  const members = membersByFrame(elements, slides);
  return layOut(
    elements,
    slides.filter((frame) => frame.id !== frameId),
    members,
    [target],
  );
};
